import categoryService from "app/common/services/categoryService";
import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { i18nFilter } from "app/filters/i18nFilter";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";

import type { ICategoriesData } from "./types";

const translate = i18nFilter();

const transformCustomCategories = (): ICustomCategory[] => {
    return UserCustomCategoryService.getCustomAndSharedCategories().map((customCategory) => ({
        ...customCategory,
        editable: true,
        deletable: true,
    }));
};

export const getCategoriesData = (selectedCategoryId?: string): ICategoriesData => {
    const decodedCategoryId = selectedCategoryId ? decodeURIComponent(selectedCategoryId) : "";

    return {
        categories: categoryService.getCategories(),
        customCategories: transformCustomCategories(),
        categoriesTitle: categoryService.getCategoriesTitle(),
        customCategoriesTitle: translate("customcategories.dropdown.title"),
        selectedCategoryId: decodedCategoryId,
    };
};

/**
 * Checks if a category matches the given ID
 */
const isCategoryMatch = (category: ICategory | ICustomCategory, id: string): boolean => {
    return category.id === id || category.categoryId === id || category.categoryId === id.substring(1);
};

/**
 * Recursively searches for a category in a list
 */
const findCategoryInList = (
    id: string,
    categories: (ICategory | ICustomCategory)[],
): ICategory | ICustomCategory | null => {
    for (const category of categories) {
        if (isCategoryMatch(category, id)) {
            return category;
        }

        if (category.children) {
            const foundInChildren = findCategoryInList(id, category.children as (ICategory | ICustomCategory)[]);
            if (foundInChildren) {
                return foundInChildren;
            }
        }
    }

    return null;
};

export const findSelectedCategory = (
    selectedId: string,
    categories: ICategory[],
    customCategories: ICustomCategory[],
): ICategory | ICustomCategory | null => {
    if (!selectedId) {
        return null;
    }

    // Search in custom categories first
    const selectedCustomCategory = findCategoryInList(selectedId, customCategories);
    if (selectedCustomCategory) {
        return selectedCustomCategory;
    }

    // Then search in regular categories
    return findCategoryInList(selectedId, categories);
};

const isSharedCustomCategory = (categoryId?: string): boolean => {
    if (!categoryId) {
        return false;
    }

    const customCategory = UserCustomCategoryService.getCustomAndSharedCategoryById(categoryId);
    return customCategory?.isShared ?? false;
};
